/**
 * Reading products, with the category and tax rate a list screen actually shows.
 *
 * WHY THE JOIN IS DONE HERE AND NOT IN THE SCREEN
 * ------------------------------------------------
 * `Product` stores `categoryId` and `taxRateId`, not names. A user recognises
 * "Electronics" and "18%", never an id, so every list has to resolve both — and
 * if each screen did it itself, they would each do it slightly differently and
 * one of them would forget the company filter.
 *
 * THREE QUERIES, NOT ONE PER ROW. A naive join is an N+1: 921 products would be
 * 1,843 lookups, the kind of cost invisible with three rows and ruinous with
 * the real backup's catalogue. So all three tables are read once, scoped to the
 * business, and indexed into Maps.
 *
 * A DANGLING REFERENCE IS A REAL STATE, NOT AN ERROR. The migration can produce a
 * product whose category was not imported, and a re-import can retire one. The
 * row still shows, with the missing piece blank — hiding a product because its
 * category is gone would hide sellable stock.
 */
import type { Table } from 'dexie';
import type { Category, Product, TaxRate } from '../db/types';

export class ProductReadError extends Error {
  constructor(
    readonly code: 'NOT_FOUND' | 'WRONG_BUSINESS',
    message: string,
  ) {
    super(message);
    this.name = 'ProductReadError';
  }
}

export interface ProductReadTables {
  products: Table<Product, string>;
  categories: Table<Category, string>;
  taxRates: Table<TaxRate, string>;
}

/** A product plus what the list shows instead of its two foreign keys. */
export interface ProductListItem {
  readonly product: Product;
  /** null when the category is missing or belongs to another company. */
  readonly categoryName: string | null;
  readonly taxRateName: string | null;
  /** Basis points, for the caller to format. null when unresolved. */
  readonly taxRateBp: number | null;
}

export interface ListProductsOptions {
  /** 'GOODS' or 'SERVICE'. Omitted means both. */
  readonly kind?: Product['kind'];
  readonly activeOnly?: boolean;
  /** Case-insensitive substring over name, SKU and HSN/SAC. */
  readonly search?: string;
  /** Narrow to one category. */
  readonly categoryId?: string;
  readonly limit?: number;
}

/** Default 500: past this a list screen is a report, not a list. */
const DEFAULT_LIMIT = 500;

export async function listProducts(
  db: ProductReadTables,
  businessId: string,
  options: ListProductsOptions = {},
): Promise<ProductListItem[]> {
  const { kind, activeOnly = false, search, categoryId } = options;
  const limit = Math.max(0, options.limit ?? DEFAULT_LIMIT);
  if (limit === 0) return [];

  // All three scoped to the SAME business. This is the whole isolation story:
  // the maps below are built only from this company's rows, so a foreign key
  // simply misses rather than resolving to another company's category.
  const [products, categories, taxRates] = await Promise.all([
    db.products.where('businessId').equals(businessId).toArray(),
    db.categories.where('businessId').equals(businessId).toArray(),
    db.taxRates.where('businessId').equals(businessId).toArray(),
  ]);

  const categoryById = new Map(categories.map((c) => [c.id, c]));
  const taxById = new Map(taxRates.map((t) => [t.id, t]));

  let rows = products;
  if (kind !== undefined) rows = rows.filter((p) => p.kind === kind);
  if (activeOnly) rows = rows.filter((p) => p.isActive);
  if (categoryId !== undefined) rows = rows.filter((p) => p.categoryId === categoryId);

  const q = search?.trim().toLowerCase();
  if (q) {
    rows = rows.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.sku.toLowerCase().includes(q) ||
        (p.hsnSac?.toLowerCase().includes(q) ?? false),
    );
  }

  // Total order: name then SKU. Two products may share a name in a real
  // catalogue, and an unstable order makes a click land on a different product
  // between renders — which on a price list means editing the wrong one.
  return rows
    .sort((a, b) => a.name.localeCompare(b.name) || a.sku.localeCompare(b.sku))
    .slice(0, limit)
    .map((product) => {
      const category = product.categoryId ? categoryById.get(product.categoryId) : undefined;
      const tax = taxById.get(product.taxRateId);
      return {
        product,
        categoryName: category?.name ?? null,
        taxRateName: tax?.name ?? null,
        taxRateBp: tax?.rateBp ?? null,
      };
    });
}

/** The company's categories, for a filter dropdown. */
export async function listCategories(
  db: Pick<ProductReadTables, 'categories'>,
  businessId: string,
): Promise<Category[]> {
  const rows = await db.categories.where('businessId').equals(businessId).toArray();
  return rows.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
}

/** The company's tax rates, for a filter dropdown. Cheapest first. */
export async function listTaxRates(
  db: Pick<ProductReadTables, 'taxRates'>,
  businessId: string,
): Promise<TaxRate[]> {
  const rows = await db.taxRates.where('businessId').equals(businessId).toArray();
  return rows.sort((a, b) => a.rateBp - b.rateBp);
}

/**
 * One product, with its resolved category and rate.
 *
 * @throws ProductReadError NOT_FOUND, or WRONG_BUSINESS when the id exists but
 *         belongs to a different company.
 */
export async function loadProduct(
  db: ProductReadTables,
  businessId: string,
  productId: string,
): Promise<ProductListItem> {
  const product = await db.products.get(productId);
  if (!product) {
    throw new ProductReadError('NOT_FOUND', `No product "${productId}" exists.`);
  }
  if (product.businessId !== businessId) {
    throw new ProductReadError(
      'WRONG_BUSINESS',
      'That product belongs to another company, not the one currently open. Refusing, ' +
        "because selling another company's stock under this letterhead puts the wrong " +
        'GSTIN on a real tax document.',
    );
  }
  const [category, tax] = await Promise.all([
    product.categoryId
      ? db.categories
          .where('businessId')
          .equals(businessId)
          .and((c) => c.id === product.categoryId)
          .first()
      : Promise.resolve(undefined),
    db.taxRates.where('businessId').equals(businessId).and((t) => t.id === product.taxRateId).first(),
  ]);
  return {
    product,
    categoryName: category?.name ?? null,
    taxRateName: tax?.name ?? null,
    taxRateBp: tax?.rateBp ?? null,
  };
}
